import axios from "axios";
import auth from "../utils/auth";
import axiosApiInstance from "../utils/axios";
import { DefaultLocation } from "../utils/constants";
import { getParkingFilterQueryParams } from "../utils/map";

const API_BASE_URL = window.REACT_APP_API_GATEWAY_URL;

const publicApiService = axios.create({
  baseURL: API_BASE_URL,
});

export const getAllParkingAreas = async () =>
  publicApiService.get(`/parkit/v2/public/parking-areas`);

export const getParkingArea = async (id) =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/parking-areas/${id}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getPublicParkingAreaById = async (id) =>
  publicApiService.get(`/parkit/v2/public/parking-areas/${id}`);

export const getParkingAreaBySlug = async (slug) =>
  publicApiService.get(`/parkit/v2/public/parking-areas/slug/${slug}`);

export const getNearbyParkingAreas = async (location, rangeInKm) =>
  publicApiService.get(
    `/parkit/v2/public/parking-areas?latitude=${
      location?.lat ? location.lat : DefaultLocation.lat
    }&longitude=${
      location?.lng ? location.lng : DefaultLocation.lng
    }&distance=${rangeInKm}${getParkingFilterQueryParams()}`,
  );

export const getFeaturedParkings = async (latitude, longitude) =>
  publicApiService.get(
    `/parkit/v2/public/parking-areas/featured?latitude=${
      latitude ?? DefaultLocation.lat
    }&longitude=${longitude ?? DefaultLocation.lng}&distance=10`
  );

export const getAdminFees = async (currency) =>
  publicApiService.get(
    `${API_BASE_URL}/parkit/v2/public/admin-fees?currency=${currency}`
  );

export const getUserVehicles = async () =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/vehicles`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const createUserVehicle = async (data) =>
  axiosApiInstance.post(`${API_BASE_URL}/parkit/v2/vehicles`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const deleteUserVehicle = async (id) =>
  axiosApiInstance.delete(`${API_BASE_URL}/parkit/v2/vehicles/${id}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const rentParking = async (data) =>
  axiosApiInstance.post(`${API_BASE_URL}/parkit/v2/rentings`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getRentings = async (page, size, filter = '') =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/rentings?page=${page}&size=${size}${filter}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getRentingById = async (id) =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/rentings/${id}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const updateRenting = async (data) =>
  axiosApiInstance.put(`${API_BASE_URL}/parkit/v2/rentings`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const deleteRenting = async (id) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/terminate`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const terminateRenting = async (id, data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/terminate`,
    data,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const cancelTerminateRenting = async (id) =>
  axiosApiInstance.delete(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/terminate`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const enquireParking = async (data) =>
  axiosApiInstance.post(`${API_BASE_URL}/parkit/v2/enquiries`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const enquireBulkListing = async (data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/bulk-listing-enquiry-form`,
    data
  );

export const enquireBulkRenting = async (data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/bulk-renting-enquiry-form`,
    data
  );

export const createListing = async (data) =>
  axiosApiInstance.post(`${API_BASE_URL}/parkit/v2/listings`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const updateListing = async (data, id) =>
  axiosApiInstance.put(`${API_BASE_URL}/parkit/v2/listings/${id}`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getListings = async (page, size, filter = '') =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/listings?page=${page}&size=${size}${filter}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getListingById = async (id) =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/listings/${id}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const freezeListing = async (id) =>
  axiosApiInstance.put(
    `${API_BASE_URL}/parkit/v2/listings/${id}/freeze`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const unfreezeListing = async (id) =>
  axiosApiInstance.put(
    `${API_BASE_URL}/parkit/v2/listings/${id}/unfreeze`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const sendUnfreezeListingNotification = async (id, notifyRentingId) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/listings/${id}/actions/unfreeze/notification${notifyRentingId ? '?rentingId=' + notifyRentingId : ''}`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const getEarliestTerminationDate = async (id) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/get-termination-date`,
    {
      headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
    }
  );

export const sendEnquiryNowNotification = async (id) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/enquiry-now-notification`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const deleteListing = async (id) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/listings/${id}/terminate`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const terminateListing = async (id, data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/listings/${id}/terminate`,
    data,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const cancelTerminateListing = async (id) =>
  axiosApiInstance.delete(
    `${API_BASE_URL}/parkit/v2/listings/${id}/terminate`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const getAppointment = async (appointmentId) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/appointments/${appointmentId}`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const prepareBooking = async (id) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/prepare-booking`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const makeBooking = async (id) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/make-booking`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const preparePayment = async (id, data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/prepare-payment`,
    data,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const makePayment = async (id, data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/rentings/${id}/make-payment`,
    data,
    {
      headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
    }
  );

export const getCustomer = async (id) =>
  axiosApiInstance.get(`${API_BASE_URL}/parkit/v2/customers/${id}`, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const getCustomerByUserName = async (userName) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/customers?userName=${userName}`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const updateCustomer = async (data) =>
  axiosApiInstance.put(`${API_BASE_URL}/parkit/v2/customers/update`, data, {
    headers: { Authorization: `Bearer ${await auth.getAccessToken()}` },
  });

export const subscribeInsurance = async (data) =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/insurances/subscribe`,
    data,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const unsubscribeInsurance = async () =>
  axiosApiInstance.post(
    `${API_BASE_URL}/parkit/v2/insurances/unsubscribe`,
    {},
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const getDeliveryFees = async (distance) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/public/delivery-fees/${distance}`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const getPayouts = async (page, size, sort, sortBy) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/payouts?page=${page}&size=${size}&sort=${sort}&sortBy=${sortBy}`,
    { headers: { Authorization: `Bearer ${await auth.getAccessToken()}` } }
  );

export const downloadPayout = async (id) =>
  axiosApiInstance.get(
    `${API_BASE_URL}/parkit/v2/payouts/${id}/download`,
    {
      headers: { 
        "Content-Type": "application/pdf",
        Authorization: `Bearer ${await auth.getAccessToken()}`,
      },
      responseType: 'blob',
    }
  );
